import React, { useEffect, useRef } from "react";
import L from "leaflet";
import { Tooltip } from "antd";
import styled from "styled-components";
import { isValidGeometry } from "utils/isValidGeometry";

const MiniMapWrapper = styled.div`
  cursor: pointer;
  border-radius: 4px;
  overflow: hidden;
  border: 1.5px solid #52c41a;
  flex-shrink: 0;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:hover {
    border-color: #389e0d;
    box-shadow: 0 1px 4px rgba(82, 196, 26, 0.35);
  }
  .leaflet-container {
    height: 100%;
    width: 100%;
    background: #e6f7e6;
  }
  .leaflet-control-container {
    display: none !important;
  }
`;

interface Props {
  surveyArea: any;
  onClick: () => void;
}

const SurveyAreaMiniMap = ({ surveyArea, onClick }: Props) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapContainerRef.current || !isValidGeometry(surveyArea)) return;

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const map = L.map(mapContainerRef.current, {
      zoomControl: false,
      attributionControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false,
      keyboard: false,
      boxZoom: false,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
    }).addTo(map);

    const geoJsonLayer = L.geoJSON(
      { type: "Feature", geometry: surveyArea, properties: {} } as any,
      {
        style: {
          color: "#1890ff",
          weight: 2,
          fillColor: "#1890ff",
          fillOpacity: 0.25,
        },
      }
    ).addTo(map);

    map.fitBounds(geoJsonLayer.getBounds(), { padding: [4, 4] });
    mapRef.current = map;

    setTimeout(() => map.invalidateSize(), 100);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [surveyArea]);

  if (!isValidGeometry(surveyArea)) return null;

  return (
    <Tooltip title="Click to edit survey area">
      <MiniMapWrapper
        onClick={onClick}
        style={{ width: 80, height: 32 }}
      >
        <div ref={mapContainerRef} style={{ width: "100%", height: "100%" }} />
      </MiniMapWrapper>
    </Tooltip>
  );
};

export default SurveyAreaMiniMap;
